<template>
    <div id="productionprogresspage">
        <div class="header-row">
            <ui5-button
                v-if="hasRight('admin') || hasRight('productionmanagement')"
                design="Default"
                @click="openUpdateModal"
                class="mr-3"
            >
                {{ trans('pages.production_progress.update_date_button') }}
            </ui5-button>

            <div class="date-input-container">
                <p>{{ trans('pages.production_progress.date') }}</p>
                <date-input
                    class="date-input"
                    name="productionProgresssDate"
                    :placeholder="trans('pages.production_progress.date')"
                    v-model="selectedDate"
                />
            </div>

            <div id="refresh-production-progress-container">
                <ui5-button
                    id="refresh-production-progress"
                    @click="refresh"
                >
                    <i
                        class="fa fa-refresh"
                        aria-hidden="true"
                    />
                </ui5-button>
            </div>
        </div>
        <div class="uses-tailwind px-5">
            <production-progress-container
                :date-string="selectedDate"
                ref="productionProgressContainer"
            />
        </div>

        <modal
            :show="showUpdateModal"
            :loading="isLoadingProducts || isUpdating"
            :loadingText="isUpdating ? trans('pages.production_progress.updating') : trans('pages.production_progress.loading_products')"
            :title="trans('pages.production_progress.update_date_modal_title')"
            @close="closeUpdateModal"
        >
            <div
                v-if="modalProducts.length > 0"
                class="warning-message uses-tailwind mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded"
            >
                <p
                    class="text-sm text-yellow-800 m-0"
                    v-html="trans('pages.production_progress.update_date_warning')"
                ></p>
            </div>

            <div
                v-if="modalProducts.length === 0 && !isLoadingProducts"
                class="uses-tailwind text-center py-8 text-gray-500"
            >
                {{ trans('pages.production_progress.no_products_to_update') }}
            </div>

            <template v-if="modalProducts.length > 0">
                <div class="mb-4 text-sm uses-tailwind">
                    {{ trans_choice('pages.production_progress.products_count', modalProducts.length, { count: modalProducts.length }) }}
                    <span
                        v-if="cannotMoveCount > 0"
                        class="text-red-600 font-medium"
                    >
                        ({{ trans_choice('pages.production_progress.cannot_move_count', cannotMoveCount, { count: cannotMoveCount }) }})
                    </span>
                </div>

                <production-date-update-table :products="modalProducts" />
            </template>

            <template #footer>
                <div class="flex items-center justify-end gap-2 p-4">
                    <ui5-button
                        design="Transparent"
                        :disabled="isUpdating"
                        @click="closeUpdateModal"
                    >
                        {{ trans('general.close') }}
                    </ui5-button>
                    <ui5-button
                        design="Default"
                        :disabled="isUpdating || modalProducts.length === 0"
                        @click="updateProductionDates"
                    >
                        <spinner
                            v-if="isUpdating"
                            :cover="false"
                            class="inline-block mr-2"
                            style="width: 16px; height: 16px;"
                        />
                        {{ trans('pages.production_progress.production_date_plus_one') }}
                    </ui5-button>
                </div>
            </template>
        </modal>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import api from '../api/productionProgress';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import DateInput from '../components/DateInput';
import localization from '../mixins/localization';
import ProductionProgressContainer from '../components/production-progress/ProductionProgressContainer';
import ProductionDateUpdateTable from '../components/production-progress/ProductionDateUpdateTable';
import Modal from '../components/modals/Modal';
import Spinner from '../components/loader/Spinner';
import { showInfoMessage, showErrorMessage } from '../dialogs';

export default {
    mixins: [NavigationFunctionsMixin, localization],
    components: {
        DateInput,
        ProductionProgressContainer,
        ProductionDateUpdateTable,
        Modal,
        Spinner,
    },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        date() {
            return moment(this.selectedDate);
        },
        cannotMoveCount() {
            return this.modalProducts.filter((p) => p.cannotMove).length;
        },
    },
    methods: {
        refresh() {
            this.$refs.productionProgressContainer.fetchData();
        },
        openUpdateModal() {
            this.showUpdateModal = true;
            this.fetchModalProducts();
        },
        closeUpdateModal() {
            this.showUpdateModal = false;
            this.modalProducts = [];
        },
        fetchModalProducts() {
            this.isLoadingProducts = true;
            api.fetchNotFinishedProductsByDate(this.date)
                .then((response) => {
                    this.modalProducts = response.data.data;
                })
                .catch((error) => {
                    console.error('Error fetching products for update:', error);
                    showErrorMessage(this.trans('pages.production_progress.fetch_error'));
                })
                .finally(() => {
                    this.isLoadingProducts = false;
                });
        },
        updateProductionDates() {
            this.isUpdating = true;
            api.incrementProductionDatesByOne(this.date)
                .then((response) => {
                    const updatedCount = response.data.data.updated;
                    const newDate = response.data.data.newDate;
                    const formattedDate = moment(newDate).format('DD/MM/YYYY');

                    // Close modal
                    this.closeUpdateModal();

                    // Jump to current day and refresh
                    this.selectedDate = new Date().toISOString();
                    this.$nextTick(() => {
                        if (this.$refs.productionProgressContainer) {
                            this.$refs.productionProgressContainer.fetchData();
                        }
                    });

                    // Show success message
                    showInfoMessage(
                        this.trans_choice('pages.production_progress.update_success', updatedCount, {
                            count: updatedCount,
                            date: formattedDate,
                        })
                    );
                })
                .catch((error) => {
                    console.error('Error updating production dates:', error);
                    showErrorMessage(this.trans('pages.production_progress.update_error'));
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
    data() {
        return {
            selectedDate: new Date().toISOString(),
            showUpdateModal: false,
            modalProducts: [],
            isLoadingProducts: false,
            isUpdating: false,
        };
    },
};
</script>
